import {
  listDocs,
  getDoc,
  createDoc,
  updateDoc,
  deleteDoc,
  runQuery
} from './appDataClient';
import { pollingSubscription } from './adapters';

// Query templates and saved results are stored through /api/app-data.
// executeQuery runs server-side against a whitelisted set of collections.

const TEMPLATES = 'queryTemplates';
const SAVED = 'savedQueries';

// ===== Query template CRUD =====

export const createQueryTemplate = async (templateData) =>
  createDoc(TEMPLATES, templateData);

export const getQueryTemplate = async (templateId) =>
  getDoc(TEMPLATES, templateId);

export const getAccountQueryTemplates = async (accountId) =>
  listDocs(TEMPLATES, { accountId });

export const getAllQueryTemplates = async () => listDocs(TEMPLATES);

export const updateQueryTemplate = async (templateId, updates) =>
  updateDoc(TEMPLATES, templateId, updates);

export const deleteQueryTemplate = async (templateId) =>
  deleteDoc(TEMPLATES, templateId);

// ===== Execution =====

export const executeQuery = async (template) => {
  try {
    const { collection, conditions, orderBy, limit } = template;
    const response = await runQuery({ collection, conditions, orderBy, limit });

    let processedResults = response.results;
    if (template.groupBy && template.groupBy.enabled) {
      processedResults = groupAndAggregateData(processedResults, template.groupBy);
    }

    return {
      success: true,
      results: processedResults,
      recordCount: response.recordCount
    };
  } catch (error) {
    console.error('Error executing query:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      results: [],
      recordCount: 0
    };
  }
};

const groupAndAggregateData = (data, groupByConfig) => {
  const { field, aggregations } = groupByConfig;

  // Group data by field
  const grouped = {};
  data.forEach(item => {
    const key = item[field] || 'N/A';
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(item);
  });

  // Calculate aggregations for each group
  const results = Object.keys(grouped).map(groupKey => {
    const groupData = grouped[groupKey];
    const result = {
      [field]: groupKey,
      count: groupData.length
    };

    // Calculate aggregations
    if (aggregations && aggregations.length > 0) {
      aggregations.forEach(agg => {
        const values = groupData
          .map(item => item[agg.field])
          .filter(val => val !== undefined && val !== null);

        if (values.length === 0) {
          result[`${agg.type}_${agg.field}`] = 0;
          return;
        }

        switch (agg.type) {
          case 'sum':
            result[`${agg.type}_${agg.field}`] = values.reduce((sum, val) => sum + Number(val), 0);
            break;
          case 'avg':
            result[`${agg.type}_${agg.field}`] = values.reduce((sum, val) => sum + Number(val), 0) / values.length;
            break;
          case 'max':
            result[`${agg.type}_${agg.field}`] = Math.max(...values.map(Number));
            break;
          case 'min':
            result[`${agg.type}_${agg.field}`] = Math.min(...values.map(Number));
            break;
          case 'count':
            result[`${agg.type}_${agg.field}`] = values.length;
            break;
          default:
            break;
        }
      });
    }

    return result;
  });

  return results;
};

export const sortResults = (data, sortField, sortDirection) => {
  return [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (aVal === bVal) return 0;

    const comparison = aVal > bVal ? 1 : -1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });
};

export const filterResults = (data, filterField, filterValue) => {
  if (!filterValue) return data;

  return data.filter(item => {
    const value = item[filterField];
    if (typeof value === 'string') {
      return value.toLowerCase().includes(filterValue.toLowerCase());
    }
    return String(value).includes(filterValue);
  });
};

// ===== Saved Query Results =====

// ===== Saved query results =====

export const saveQueryResult = async (resultData) => createDoc(SAVED, resultData);

export const getSavedQueryResults = async (accountId) => listDocs(SAVED, { accountId });

export const deleteSavedQuery = async (queryId) => deleteDoc(SAVED, queryId);

export const subscribeToQueryTemplates = (accountId, callback) =>
  pollingSubscription(() => getAccountQueryTemplates(accountId), callback);

export const getAvailableCollections = () => {
  return [
    { value: 'timers', label: '計時器' },
    { value: 'devices', label: '設備' },
    { value: 'accounts', label: '帳號' },
    { value: 'backupSources', label: '備份來源' },
    { value: 'backupLogs', label: '備份記錄' }
  ];
};

export const getOperators = () => {
  return [
    { value: '==', label: '等於' },
    { value: '!=', label: '不等於' },
    { value: '>', label: '大於' },
    { value: '<', label: '小於' },
    { value: '>=', label: '大於等於' },
    { value: '<=', label: '小於等於' },
    { value: 'array-contains', label: '包含' },
    { value: 'in', label: '在...之中' }
  ];
};

export const getAggregationTypes = () => {
  return [
    { value: 'sum', label: '總和' },
    { value: 'avg', label: '平均' },
    { value: 'max', label: '最大' },
    { value: 'min', label: '最小' },
    { value: 'count', label: '計數' }
  ];
};

export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) {
    alert('沒有資料可以匯出');
    return;
  }

  // Get all keys from first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  // Create blob and download
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const printResults = (data) => {
  if (!data || data.length === 0) {
    alert('沒有資料可以列印');
    return;
  }

  const printWindow = window.open('', '_blank');
  const headers = Object.keys(data[0]);

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>查詢結果</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #2d3748; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; }
        th { background: #f7fafc; font-weight: 600; }
        tr:hover { background: #f7fafc; }
        @media print {
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <h1>查詢結果</h1>
      <p>總筆數：${data.length}</p>
      <p>列印時間：${new Date().toLocaleString('zh-TW')}</p>
      <table>
        <thead>
          <tr>
            ${headers.map(h => `<th>${h}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${data.map(row => `
            <tr>
              ${headers.map(h => `<td>${row[h] || '-'}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 250);
};
